// Las claves de traducción para name, defaultTitle y description están en:
// locales/{es,en,pt}.json -> componentsMap.[id].{name,defaultTitle,description}
/**
 * Mapa de configuración de componentes para el Panel Dinámico.
 *
 * Este objeto asocia identificadores únicos de componentes (IDs) con sus respectivas
 * configuraciones, rutas de importación y claves de localización.
 *
 * Cada entrada en el mapa contiene:
 * - `nameKey`: Clave de i18n para el nombre del componente.
 * - `importPath`: Ruta relativa para importar el componente (generalmente para carga dinámica).
 * - `defaultTitleKey`: Clave de i18n para el título predeterminado.
 * - `img`: Nombre del archivo de imagen de previsualización asociado.
 * - `descriptionKey`: Clave de i18n para la descripción del componente.
 *
 * @constant {Object.<string, {
 *   nameKey: string,
 *   importPath: string,
 *   defaultTitleKey: string,
 *   img: string,
 *   descriptionKey: string
 * }>}
 *
 * @property {string} nameKey - La clave de traducción i18n para el nombre del componente.
 * @property {string} importPath - La ruta relativa del archivo para importar el componente Vue.
 * @property {string} defaultTitleKey - La clave de traducción i18n para el título predeterminado mostrado en la tarjeta del dashboard.
 * @property {string} img - El nombre del archivo de imagen de previsualización asociado al componente (ubicado en los assets públicos).
 * @property {string} descriptionKey - La clave de traducción i18n para la descripción de la funcionalidad del componente.
 *
 * @author David Maldonado
 * (c) Servicios de Monitoreo S.A. - Santiago, Chile
 */
export const COMPONENTS_MAP = {
  'id59-m4-s6': {
    nameKey: 'componentsMap.id59-m4-s6.name',
    importPath: '../../../../components/IndividualCharts/Gauge/Gauge',
    defaultTitleKey: 'componentsMap.id59-m4-s6.defaultTitle',
    img: 'gauge.png',
    descriptionKey: 'componentsMap.id59-m4-s6.description',
  },
  'id93-m16-s1': {
    nameKey: 'componentsMap.id93-m16-s1.name',
    importPath: '../../../HeatMap/components/HeatMapObjective',
    defaultTitleKey: 'componentsMap.id93-m16-s1.defaultTitle',
    img: 'heatmap-date.png',
    descriptionKey: 'componentsMap.id93-m16-s1.description',
  },
  'id61-m4-s6': {
    nameKey: 'componentsMap.id61-m4-s6.name',
    importPath: '../../../ConsolidatedView/ConsolidatedView',
    defaultTitleKey: 'componentsMap.id61-m4-s6.defaultTitle',
    img: 'consolidado.png',
    descriptionKey: 'componentsMap.id61-m4-s6.description',
  },
  'id100-m4-s6': {
    nameKey: 'componentsMap.id100-m4-s6.name',
    importPath: '../../../DisponibilityNodes/DisponibilityNodes',
    defaultTitleKey: 'componentsMap.id100-m4-s6.defaultTitle',
    img: 'disponibilidad-nodos.png',
    descriptionKey: 'componentsMap.id100-m4-s6.description',
  },
  'id94-m16-s2': {
    nameKey: 'componentsMap.id94-m16-s2.name',
    importPath: '../../../HeatMap/components/HeatMapObjective',
    defaultTitleKey: 'componentsMap.id94-m16-s2.defaultTitle',
    img: 'heatmap-date.png',
    descriptionKey: 'componentsMap.id94-m16-s2.description',
  },
  'id96-m4-s6': {
    nameKey: 'componentsMap.id96-m4-s6.name',
    importPath: '../../../ConsolidatedView/ConsolidatedView',
    defaultTitleKey: 'componentsMap.id96-m4-s6.defaultTitle',
    img: 'consolidado.png',
    descriptionKey: 'componentsMap.id96-m4-s6.description',
  },
  'id62-m4-s6': {
    nameKey: 'componentsMap.id62-m4-s6.name',
    importPath: '../../../ConsolidatedResponseTime/ConsolidatedResponseTime',
    defaultTitleKey: 'componentsMap.id62-m4-s6.defaultTitle',
    img: 'consolidated-response-time.png',
    descriptionKey: 'componentsMap.id62-m4-s6.description',
  },
  'id97-m4-s6': {
    nameKey: 'componentsMap.id97-m4-s6.name',
    importPath: '../../../ResponseTimeForStep/ResponseTimeForStepChart',
    defaultTitleKey: 'componentsMap.id97-m4-s6.defaultTitle',
    img: 'response-time-for-step.png',
    descriptionKey: 'componentsMap.id97-m4-s6.description',
  },
  'id98-m4-s6': {
    nameKey: 'componentsMap.id98-m4-s6.name',
    importPath: '../../../ResponseTimeForNode/ResponseTimeForNodeChart',
    defaultTitleKey: 'componentsMap.id98-m4-s6.defaultTitle',
    img: 'response-time-for-node.png',
    descriptionKey: 'componentsMap.id98-m4-s6.description',
  },
  'id105-m4-s6': {
    nameKey: 'componentsMap.id105-m4-s6.name',
    importPath: '../../../EventsMeasurements/EventsMeasurementsChart',
    defaultTitleKey: 'componentsMap.id105-m4-s6.defaultTitle',
    img: 'events-measurements.png',
    descriptionKey: 'componentsMap.id105-m4-s6.description',
  },
  'id104-m4-s6': {
    nameKey: 'componentsMap.id104-m4-s6.name',
    importPath:
      '../../../ConsolidatedMeasurementsChart/ConsolidatedMeasurementsChart',
    defaultTitleKey: 'componentsMap.id104-m4-s6.defaultTitle',
    img: 'consolidated-measurements.png',
    descriptionKey: 'componentsMap.id104-m4-s6.description',
  },
  'id64-m4-s6': {
    nameKey: 'componentsMap.id64-m4-s6.name',
    importPath:
      '../../../IndividualCharts/AbsoluteFrecuency/AbsoluteFrecuencyChart',
    defaultTitleKey: 'componentsMap.id64-m4-s6.defaultTitle',
    img: 'tiempo-respuesta-frecuencia-absoluta.png',
    descriptionKey: 'componentsMap.id64-m4-s6.description',
  },
  'id103-m4-s6': {
    nameKey: 'componentsMap.id103-m4-s6.name',
    importPath:
      '../../../IndividualCharts/CumulativeFrecuency/CumulativeFrecuencyChart',
    defaultTitleKey: 'componentsMap.id103-m4-s6.defaultTitle',
    img: 'tiempo-respuesta-frecuencia-acumulada.png',
    descriptionKey: 'componentsMap.id103-m4-s6.description',
  },
  'id65-m4-s6': {
    nameKey: 'componentsMap.id65-m4-s6.name',
    importPath:
      '../../../IndividualCharts/AvailabilityDowntime/AvailabilityDowntimeChart',
    defaultTitleKey: 'componentsMap.id65-m4-s6.defaultTitle',
    img: 'availability-downtime-global.png',
    descriptionKey: 'componentsMap.id65-m4-s6.description',
  },
  'id66-m4-s6': {
    nameKey: 'componentsMap.id66-m4-s6.name',
    importPath:
      '../../../IndividualCharts/AvailabilityDowntimeGlobal/AvailabilityDowntimeGlobalChart',
    defaultTitleKey: 'componentsMap.id66-m4-s6.defaultTitle',
    img: 'availability-downtime-global-by-step.png',
    descriptionKey: 'componentsMap.id66-m4-s6.description',
  },
  'id102-m4-s6': {
    nameKey: 'componentsMap.id102-m4-s6.name',
    importPath: './charts/AvailabilityPercentageDistribution',
    defaultTitleKey: 'componentsMap.id102-m4-s6.defaultTitle',
    img: 'availability-percentage-distribution.png',
    descriptionKey: 'componentsMap.id102-m4-s6.description',
  },
  'id106-m4-s6': {
    nameKey: 'componentsMap.id106-m4-s6.name',
    importPath: './charts/AvailabilityPercentageDistribution',
    defaultTitleKey: 'componentsMap.id106-m4-s6.defaultTitle',
    img: 'distribution-consolidated-chart.png',
    descriptionKey: 'componentsMap.id106-m4-s6.description',
  },
  'id107-m4-s6': {
    nameKey: 'componentsMap.id107-m4-s6.name',
    importPath: 'src/views/Online/components/Availability/AvailabilityDowntimeGlobalWithError/index.jsx',
    defaultTitleKey: 'componentsMap.id107-m4-s6.defaultTitle',
    img: 'availability-downtime-global-with-error.png',
    descriptionKey: 'componentsMap.id107-m4-s6.description',
  }
};
